// Athena beside the page (operator request, 23 Oct): "Ask Athena" used
// to navigate to /shell/copilot, so asking anything meant leaving what
// you were looking at. The panel opens on the right of every shell
// page, keeps one conversation across pages (AthenaProvider) and tells
// Athena which page it is on.
//
// From 1024px it sits beside the page and the page narrows; below that
// it covers the page with a backdrop. Escape closes it and returns
// focus to whatever opened it.

import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Maximize2, RotateCcw, Sparkles, X } from 'lucide-react';
import { AthenaChat } from './AthenaChat';
import { useAthena } from './AthenaProvider';
import { pageContextPath } from '../../lib/athenaHistory';

export function AthenaPanel({ pathname, title }: { pathname: string; title: string }) {
  const { panelOpen, setPanelOpen, persona, reset, sending, messages } = useAthena();
  const panelRef = useRef<HTMLElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const name = persona?.name ?? 'Athena';
  const path = pageContextPath(pathname);
  const page = path ? { path, title: title.slice(0, 120) } : undefined;

  useEffect(() => {
    if (!panelOpen) return;
    returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    return () => {
      returnFocusRef.current?.focus?.();
    };
  }, [panelOpen]);

  if (!panelOpen) return null;

  const close = () => setPanelOpen(false);

  return (
    <>
      <div
        onClick={close}
        className="fixed inset-0 z-40 bg-background/60 backdrop-blur-[1px] lg:hidden"
        aria-hidden="true"
      />
      <aside
        ref={panelRef}
        aria-label={`${name}, AI copilot`}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.stopPropagation();
            close();
          }
        }}
        className="fixed inset-y-0 right-0 z-50 flex w-full max-w-[26rem] flex-col border-l border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900 lg:sticky lg:top-0 lg:z-auto lg:h-screen lg:w-[24rem] lg:max-w-none lg:shrink-0 lg:shadow-none"
      >
        <div className="flex items-center gap-2 border-b border-slate-200 px-3 py-2.5 dark:border-slate-700">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-violet-100 text-violet-700 ring-1 ring-violet-200 dark:bg-violet-950/50 dark:text-violet-300 dark:ring-violet-900">
            <Sparkles className="h-4 w-4" aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1 leading-tight">
            <div className="text-sm font-semibold text-foreground">{name}</div>
            <div className="truncate text-[11px] text-muted-foreground">
              {page?.title ? `Asking about ${page.title}` : 'Your security assistant'}
            </div>
          </div>
          <button
            type="button"
            onClick={reset}
            disabled={sending || messages.length <= 1}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40"
            aria-label="New conversation"
            title="New conversation"
          >
            <RotateCcw className="h-4 w-4" aria-hidden="true" />
          </button>
          <Link
            to="/shell/copilot"
            onClick={close}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="Open full page"
            title="Open full page"
          >
            <Maximize2 className="h-4 w-4" aria-hidden="true" />
          </Link>
          <button
            type="button"
            onClick={close}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={`Close ${name}`}
            title="Close (Esc)"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
        <AthenaChat variant="panel" page={page} />
      </aside>
    </>
  );
}
